import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from './3SCLandingPageB.module.css';
import { faqData, benefitsData } from '../constants';

// Icon components
const CheckIcon = () => (
  <svg 
    className={styles.benefitIcon}
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5"></path>
  </svg>
);

const CompletedIcon = () => (
  <svg 
    className={styles.completedIcon}
    xmlns="http://www.w3.org/2000/svg" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <path d="M20 6L9 17l-5-5"></path>
  </svg>
);

const ChevronDownIcon = ({ expanded }) => (
  <svg 
    className={`${styles.faqIcon} ${expanded ? styles.expanded : ''}`}
    xmlns="http://www.w3.org/2000/svg" 
    width="20" 
    height="20" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round"
  >
    <polyline points="6,9 12,15 18,9"></polyline>
  </svg>
);

const SCProgressLandingPage = () => {
  const navigate = useNavigate();
  const [expandedFAQ, setExpandedFAQ] = useState(null);

  const handleResumeClick = () => {
    navigate('/sustainability/questionnaire');
  };

  const toggleFAQ = (index) => {
    setExpandedFAQ(expandedFAQ === index ? null : index);
  };

  // Progress data - this would typically come from your backend/state
  const progressData = {
    currentStep: 1, // Current step (1-4)
    currentQuestion: 14,
    totalQuestions: 27,
    lastSaved: "25/08/2024",
    completionPercentage: 35, // Overall completion percentage
    steps: [
      { number: 1, label: "Questionnaire", status: "current" },
      { number: 2, label: "Upload Docs", status: "pending" },
      { number: 3, label: "Review", status: "pending" },
      { number: 4, label: "Badge Ready", status: "pending" }
    ]
  };

  const historyData = [
    { step: "Questionnaire", completedDate: "12 Jun", status: "completed" },
    { step: "Docs Submitted", completedDate: "13 Jun", status: "completed" }
  ];

  const getStepIcon = (step, index) => {
    if (step.status === 'completed') {
      return <CheckIcon />;
    } else if (step.status === 'current') {
      return step.number;
    } else {
      return step.number;
    }
  };

  return (
    <div className={styles.landingPage}>
      {/* Progress Section (Top Block) */}
      <div className={styles.progressSection}>
        <div className={styles.progressCard}>
          <h1 className={styles.currentStatus}>
            <span className={styles.statusHighlight}>Step 1: Questionnaire</span> – In Progress
          </h1>
          
          <button className={styles.resumeButton} onClick={handleResumeClick}>
            Resume from where I left off
          </button>
          
          <p className={styles.lastSaved}>
            Last saved on {progressData.lastSaved}
          </p>

          {/* Progress Bar */}
          <div className={styles.progressBarContainer}>
            <div className={styles.progressSteps}>
              {progressData.steps.map((step, index) => (
                <div key={index} className={styles.progressStep}>
                  <div className={`${styles.stepIcon} ${styles[step.status]}`}>
                    {getStepIcon(step, index)}
                  </div>
                  <div className={styles.stepLabel}>{step.label}</div>
                  <div className={styles.stepNumber}>Step {step.number}</div>
                </div>
              ))}
            </div>
            
            <div className={styles.progressBar}>
              <div 
                className={styles.progressFill} 
                style={{ width: `${progressData.completionPercentage}%` }}
              ></div>
            </div>

            <div className={styles.subProgress}>
              <div className={styles.subProgressText}>
                Question {progressData.currentQuestion}/{progressData.totalQuestions} completed
              </div>
              <div className={styles.subProgressDetail}>
                {Math.round((progressData.currentQuestion / progressData.totalQuestions) * 100)}% of questionnaire finished
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Progress History Table */}
      <div className={styles.progressHistorySection}>
        <h2 className={styles.historyTitle}>Progress History</h2>
        <div className={styles.historyTable}>
          <table>
            <thead>
              <tr>
                <th>Step</th>
                <th>Completed Date</th>
              </tr>
            </thead>
            <tbody>
              {historyData.map((item, index) => (
                <tr key={index}>
                  <td>
                    <span className={styles.completedStatus}>
                      <CompletedIcon />
                      {item.step}
                    </span>
                  </td>
                  <td>{item.completedDate}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Header Section (Same as Variant A) */}
      <div className={styles.headerSection}>
        <h1 className={styles.title}>Sustainability Score Certificate (3SC)</h1>
        <h2 className={styles.subline}>Your proof-backed sustainability badge—built with you, not for you.</h2>
        <p className={styles.descriptor}>
          This 1–5 star badge reflects your brand's sustainable practices, verified by suspire's rigorous framework. 
          Together, we build trust that customers can see.
        </p>
      </div>

      {/* Benefits Section (Same as Variant A) */}
      <div className={styles.benefitsSection}>
        <h2 className={styles.sectionTitle}>Benefits</h2>
        <div className={styles.benefitsGrid}>
          <div className={styles.benefitColumn}>
            <h3>For Your Brand</h3>
            <ul className={styles.benefitList}>
              {benefitsData.brand.map((benefit, index) => (
                <li key={index} className={styles.benefitListItem}>
                  <CheckIcon />
                  <span className={styles.benefitText}>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className={styles.benefitColumn}>
            <h3>For Customers</h3>
            <ul className={styles.benefitList}>
              {benefitsData.customers.map((benefit, index) => (
                <li key={index} className={styles.benefitListItem}>
                  <CheckIcon />
                  <span className={styles.benefitText}>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
          
          <div className={styles.benefitColumn}>
            <h3>For suspire Marketplace</h3>
            <ul className={styles.benefitList}>
              {benefitsData.marketplace.map((benefit, index) => (
                <li key={index} className={styles.benefitListItem}>
                  <CheckIcon />
                  <span className={styles.benefitText}>{benefit}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </div>

      {/* FAQ Section (Same as Variant A) */}
      <div className={styles.faqSection}>
        <h2 className={styles.sectionTitle}>Frequently Asked Questions</h2>
        <div className={styles.faqList}>
          {faqData.map((faq, index) => (
            <div key={index} className={styles.faqItem}>
              <button 
                className={styles.faqQuestion}
                onClick={() => toggleFAQ(index)}
              >
                {faq.question}
                <ChevronDownIcon expanded={expandedFAQ === index} />
              </button>
              {expandedFAQ === index && (
                <div className={styles.faqAnswer}>
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default SCProgressLandingPage;